import FirstbiteLogo from './icons/fblogo.svg'
import TagIcon from './icons/tag.svg'
import CartIcon from './icons/cart.svg'
import InvoiceIcon from './icons/invoice.svg'
import CashIcon from './icons/cash.svg'
import AccountIcon from './icons/account.svg'
import { useMemo } from 'react'
import { capitalizeFirst } from '../../utils/formatting'
import { formatOfferCurrency, getPillText } from '../../utils/offer-formatting'
import { DistributorCode } from '../../models/product'

/**
 * Props for the OfferPage component.
 */
export interface OfferPageProps {
  /** Offer data including terms, products, and manufacturer details */
  data: any // TODO: get the correct type
  /** Callback when user clicks the claim offer button */
  onClaimOffer: () => void
  /** Whether the offer has expired */
  isExpired?: boolean
  /** Whether the offer is temporarily paused */
  isPaused?: boolean
  /** Whether this is a preview render (disables tracking) */
  isPreview?: boolean
  /** Additional CSS class for the container */
  containerClassName?: string
}

/** Types of rebate offers - determines headline and terms copy. */
enum RebateOfferType {
  TOTAL_VALUE = 'TOTAL_VALUE',
  SINGLE_CASE = 'SINGLE_CASE',
  PRICE_PER_CASE = 'PRICE_PER_CASE',
  MINIMUM_ORDER = 'MINIMUM_ORDER',
  // Multi-product offer types (per-product pricing)
  MULTI_PRODUCT_SAVINGS = 'MULTI_PRODUCT_SAVINGS',
  MULTI_PRODUCT_CASHBACK = 'MULTI_PRODUCT_CASHBACK',
  MULTI_PRODUCT_TRIAL = 'MULTI_PRODUCT_TRIAL',
  MULTI_PRODUCT_CUSTOMER_REBATE = 'MULTI_PRODUCT_CUSTOMER_REBATE',
  MULTI_PRODUCT_PROGRAM = 'MULTI_PRODUCT_PROGRAM',
  // Legacy types (backwards compatibility)
  FIRST_OFFER = 'FIRST_OFFER',
  TIME_OFFER = 'TIME_OFFER',
}

/** Branding options for how the offer subject is displayed. */
enum RebateBranding {
  COMPANY = 'COMPANY',
  COMPANY_PRODUCTS = 'COMPANY_PRODUCTS',
  SELECT_COMPANY_PRODUCTS = 'SELECT_COMPANY_PRODUCTS',
  PRODUCT = 'PRODUCT',
  PRODUCT_FROM_COMPANY = 'PRODUCT_FROM_COMPANY',
}

/**
 * Displays a single product variant card with name, image, format pill, SKU, GTIN, and distributor codes.
 */
function VariantCard({
  variant,
  hasAnyImage,
  bordered = true,
}: {
  variant: any
  hasAnyImage: boolean
  bordered?: boolean
}) {
  return (
    <div
      className={`bg-white px-6 py-6 ${bordered ? 'border border-sage-lt rounded-xl' : ''}`}
    >
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1 flex flex-col gap-2">
          <h3 className="text-2xl font-normal leading-8">
            {variant.variant_name}
          </h3>
          <div className="font-mono text-xs text-neutral-700 font-medium leading-4 tracking-wide flex flex-col">
            {variant.variant_sku && <span>SKU: {variant.variant_sku}</span>}
            {variant.variant_gtin && <span>GTIN: {variant.variant_gtin}</span>}
            {variant.distributor_codes
              ?.filter(
                (dc: DistributorCode) =>
                  dc.distributor_name && dc.distributor_code
              )
              .map((dc: DistributorCode, idx: number) => (
                <span
                  key={`${idx}-${dc.distributor_name}-${dc.distributor_code}`}
                >
                  {dc.distributor_name}: {dc.distributor_code}
                </span>
              ))}
          </div>
          {variant.variant_format && (
            <div className="mt-1">
              <span className="inline-flex rounded-2xl bg-sage-extra-lt px-3 py-2 font-mono text-xs font-medium leading-4 tracking-wide">
                {variant.variant_format}
              </span>
            </div>
          )}
        </div>
        {variant.variant_image_url ? (
          <div className="size-20 bg-white rounded-lg shadow-[0px_1px_20px_2px_rgba(0,0,0,0.05)] flex items-center justify-center overflow-hidden flex-shrink-0">
            <img
              src={variant.variant_image_url}
              alt={variant.variant_name}
              className="size-20 object-contain rounded-lg"
            />
          </div>
        ) : hasAnyImage ? (
          <div className="size-20 flex-shrink-0" />
        ) : null}
      </div>
    </div>
  )
}

/**
 * Green header bar for a product group showing per-case discount and min/max pill.
 */
function ProductGroupHeader({
  discountFormatted,
  productName,
  pillText,
}: {
  discountFormatted: string
  productName: string
  pillText: string
}) {
  return (
    <div className="bg-[rgba(40,182,67,0.25)] rounded-t-xl px-6 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2.5">
      <p className="text-emerald-900 text-base md:text-xl font-medium leading-5 tracking-tight">
        Save{' '}
        <span className="font-bold text-emerald-950">
          ${discountFormatted} per case
        </span>{' '}
        on {productName} products.
      </p>
      <span className="bg-neutral-100 border border-green-600 rounded-2xl px-3 py-2 font-mono text-xs font-medium leading-4 tracking-wide flex-shrink-0 self-start md:self-auto">
        {pillText}
      </span>
    </div>
  )
}

/** Shared card styling matching Figma design tokens. */
const SECTION_CARD =
  'bg-stone-50 p-5 md:p-10 rounded-2xl shadow-[0px_2px_8px_2px_rgba(0,0,0,0.08)] mb-6 md:mb-10'

/**
 * Renders the public-facing offer page for rebate offers.
 *
 * Supports two pricing modes:
 * - "global": All products share the same tier pricing
 * - "per_product": Each product has its own tier pricing
 *
 * Also supports uncapped offers (max === null) with "no maximum" messaging.
 */
export function OfferPage({
  data,
  onClaimOffer,
  isExpired,
  isPaused,
  containerClassName,
}: OfferPageProps) {
  const tier = data.offer?.terms?.tiers?.[0]
  const products = data.offer?.terms?.products
  const pricingMode = data.offer?.terms?.pricing_mode || 'global'
  const isPerProduct = pricingMode === 'per_product'

  // Check if any variant has an image
  const hasAnyImage = useMemo(() => {
    return products?.some((product: any) =>
      product.product_variants?.some(
        (variant: any) => variant.variant_image_url
      )
    )
  }, [products])

  // Get branding text based on branding type
  const getBrandingText = useMemo(() => {
    const branding = data.offer?.branding || RebateBranding.COMPANY
    const companyName = data.offer?.manufacturer_details?.name
    // Get the actual product name from the terms data
    const firstProduct = data.offer?.terms?.products?.[0]
    const productName =
      firstProduct?.product_name || products?.[0]?.product_name || 'Product'

    switch (branding) {
      case RebateBranding.COMPANY:
        return companyName
      case RebateBranding.COMPANY_PRODUCTS:
        return `${companyName} products`
      case RebateBranding.SELECT_COMPANY_PRODUCTS:
        return `select ${companyName} products`
      case RebateBranding.PRODUCT:
        return productName
      case RebateBranding.PRODUCT_FROM_COMPANY:
        return `${productName} from ${companyName}`
      default:
        return companyName
    }
  }, [
    data.offer?.branding,
    data.offer?.manufacturer_details?.name,
    data.offer?.terms?.products,
    products,
  ])

  const headline = useMemo(() => {
    const offerType = data.offer?.type
    const brandingText = getBrandingText

    // For per-product pricing, use the multi-product offer type headlines
    if (isPerProduct) {
      switch (offerType) {
        case RebateOfferType.MULTI_PRODUCT_SAVINGS:
          return <>Special savings on {brandingText}</>
        case RebateOfferType.MULTI_PRODUCT_CASHBACK:
          return <>Cash back offer on {brandingText}</>
        case RebateOfferType.MULTI_PRODUCT_TRIAL:
          return <>Special trial offer on {brandingText}</>
        case RebateOfferType.MULTI_PRODUCT_CUSTOMER_REBATE:
          return <>Rebate for {brandingText}</>
        case RebateOfferType.MULTI_PRODUCT_PROGRAM:
          return <>{capitalizeFirst(brandingText)} Rebate Program</>
        default:
          return (
            <>
              <span className="text-success-bright">Cash back</span> on{' '}
              {brandingText}
            </>
          )
      }
    }

    // Global pricing - use existing logic
    const discount = tier?.discount
    const max = tier?.max
    const min = tier?.min || 1
    const isUncapped = max === null || max === undefined
    const total = discount * (max || 0)

    switch (offerType) {
      case RebateOfferType.TOTAL_VALUE:
        if (isUncapped) {
          return (
            <>
              <span className="text-success-bright">
                ${discount?.toLocaleString()} cash back
              </span>{' '}
              per case of {brandingText}, no maximum
            </>
          )
        }
        return (
          <>
            Up to{' '}
            <span className="text-success-bright">
              ${total.toLocaleString()} cash back
            </span>{' '}
            on your first{' '}
            {max === 1 ? 'case' : `${max?.toLocaleString()} cases`} of{' '}
            {brandingText}
          </>
        )

      case RebateOfferType.SINGLE_CASE:
        return (
          <>
            <span className="text-success-bright">${discount} cash back</span>{' '}
            on your first case of {brandingText}
          </>
        )

      case RebateOfferType.PRICE_PER_CASE:
        if (isUncapped) {
          return (
            <>
              <span className="text-success-bright">${discount} off</span> per
              case of {brandingText}, no maximum
            </>
          )
        }
        return (
          <>
            <span className="text-success-bright">${discount} off</span> per
            case of {brandingText}, up to {max} {max === 1 ? 'case' : 'cases'}
          </>
        )

      case RebateOfferType.MINIMUM_ORDER:
        if (isUncapped) {
          return (
            <>
              <span className="text-success-bright">${discount} off</span> per
              case of {brandingText}, when you buy at least {min}{' '}
              {min === 1 ? 'case' : 'cases'}, no maximum
            </>
          )
        }
        return (
          <>
            <span className="text-success-bright">${discount} off</span> per
            case of {brandingText}, when you buy at least {min}{' '}
            {min === 1 ? 'case' : 'cases'}
          </>
        )

      // Multi-product offer types (generic copy without green highlighting)
      case RebateOfferType.MULTI_PRODUCT_SAVINGS:
        return <>Special savings on {brandingText}</>

      case RebateOfferType.MULTI_PRODUCT_CASHBACK:
        return <>Cash back offer on {brandingText}</>

      case RebateOfferType.MULTI_PRODUCT_TRIAL:
        return <>Special trial offer on {brandingText}</>

      case RebateOfferType.MULTI_PRODUCT_CUSTOMER_REBATE:
        return <>Rebate for {brandingText}</>

      case RebateOfferType.MULTI_PRODUCT_PROGRAM:
        return <>{capitalizeFirst(brandingText)} Rebate Program</>

      // Legacy types
      case RebateOfferType.FIRST_OFFER:
        if (isUncapped) {
          return (
            <>
              <span className="text-success-bright">
                ${discount} off per case
              </span>{' '}
              of {brandingText} at every one of your locations, no maximum
            </>
          )
        }
        return (
          <>
            <span className="text-success-bright">${discount} off </span> your
            first{' '}
            {max === 1 ? (
              'case'
            ) : (
              <>
                <span className="text-success-bright">{max}</span> cases
              </>
            )}{' '}
            of {brandingText} at every one of your locations
          </>
        )

      case RebateOfferType.TIME_OFFER:
        return (
          <>
            <span className="text-success-bright">
              ${discount} off per case
            </span>{' '}
            of {brandingText} at every one of your locations
          </>
        )

      default:
        return (
          <>
            <span className="text-success-bright">
              ${discount} off per case
            </span>{' '}
            of {brandingText}
          </>
        )
    }
  }, [
    data.offer?.type,
    getBrandingText,
    tier?.discount,
    tier?.max,
    tier?.min,
    isPerProduct,
  ])

  const subheadline = useMemo(() => {
    const date = new Date(data.offer?.valid_to)
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
    return `Purchase by ${formattedDate} and get reimbursed*`
  }, [data.offer?.valid_to])

  const termsString = useMemo(() => {
    /**
     * Generates terms copy using template:
     * "Get $[rebate] back per case on qualifying products {min_clause} {max_clause}"
     *
     * min_clause: "when you buy at least [x] cases" (only if min > 1)
     * max_clause: "Maximum rebate value is $[calculated]." (only if max exists)
     */
    const generateTermsCopy = (
      discount: number | null | undefined,
      min: number | null | undefined,
      max: number | null | undefined
    ): string => {
      const discountValue = discount ?? 0

      // Base sentence
      let sentence = `Get $${formatOfferCurrency(discountValue)} back per case on qualifying products`

      // Add min clause if minimum > 1
      const hasMin = min != null && min > 1
      if (hasMin) {
        sentence += ` when you buy at least ${min.toLocaleString()} cases`
      }

      // Add max clause if maximum exists (not uncapped)
      const hasMax = max != null
      if (hasMax) {
        sentence += `. Maximum rebate value is $${formatOfferCurrency(max * discountValue)}.`
      } else {
        sentence += '.'
      }

      return sentence
    }

    // For per-product pricing, show pricing for each product
    if (isPerProduct && products) {
      return (
        <>
          {products.map((product: any, index: number) => {
            const productTier = product.tiers?.[0]
            if (!productTier) return null

            const termsCopy = generateTermsCopy(
              productTier.discount,
              productTier.min,
              productTier.max
            )

            return (
              <li key={index} className="mb-2">
                <strong>{product.product_name}:</strong> {termsCopy}
              </li>
            )
          })}
        </>
      )
    }

    // Global pricing
    return generateTermsCopy(tier?.discount, tier?.min, tier?.max)
  }, [tier?.min, tier?.max, tier?.discount, isPerProduct, products])

  function OfferStatusButton() {
    if (isExpired) {
      return (
        <div
          className={`bg-red-50 border-l-4 border-red-400 p-4 rounded ${containerClassName}`}
        >
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Offer Expired
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  This offer has expired and is no longer accepting new
                  enrollments. Please check back later for new offers from{' '}
                  {data.offer?.manufacturer_details.name}.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (isPaused) {
      return (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Offer Temporarily Paused
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  This offer is temporarily paused and not accepting new
                  enrollments at this time. Please check back soon as the offer
                  may become available again.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return (
      <button
        className="bg-brand text-white px-5 py-3 rounded-full flex-shrink-0"
        onClick={onClaimOffer}
      >
        Claim Offer
      </button>
    )
  }

  return (
    <div
      style={{ fontFamily: 'Inter' }}
      className="w-full bg-sage-extra-lt text-brand-deep h-full"
    >
      <div className="flex bg-brand-deep flex-col items-center justify-center pt-10 pb-20">
        <img src={FirstbiteLogo} alt="Firstbite Logo" />
      </div>

      <div className="-mt-10 px-3 md:px-10 max-w-[1000px] mx-auto">
        <div
          className={`flex ${SECTION_CARD} justify-between items-center gap-5 md:gap-10`}
        >
          <div className="flex flex-col gap-5 items-center md:items-start flex-grow">
            <span className="flex items-center gap-2 mb-3">
              <img src={TagIcon} alt="Tag Icon" />
              You&apos;ve received an offer
            </span>
            <div className="font-normal mb-5 flex flex-col md:flex-row items-center md:items-start gap-5 text-center md:text-left">
              <img
                src={data.offer?.manufacturer_details?.logo}
                alt={data.offer?.manufacturer_details?.name}
                className="w-32 h-32 bg-transparent rounded-lg object-contain md:hidden mb-3"
              />
              <div>
                <h1 className="text-3xl md:text-4xl">{headline}</h1>
                {subheadline && (
                  <p className="text-gray-600 text-md mt-6">{subheadline}</p>
                )}
              </div>
            </div>
            <div className="w-full flex justify-center md:justify-start">
              <OfferStatusButton />
            </div>
          </div>
          <img
            src={data.offer?.manufacturer_details?.logo}
            alt={data.offer?.manufacturer_details?.name}
            className="w-32 h-32 bg-transparent rounded-lg object-contain hidden md:block"
          />
        </div>

        <div className={`flex flex-col ${SECTION_CARD}`}>
          <div className="flex flex-col items-start lg:flex-row lg:justify-between lg:gap-20">
            <h2 className="text-3xl mb-5 flex-shrink-0 ">How it works</h2>
            <ul className="text-base md:text-lg ">
              <li
                className={
                  'border-b border-brand-deep border-opacity-20 pb-2 flex items-center gap-2'
                }
              >
                <img src={AccountIcon} alt="Account Icon" />
                Create an account in 30 seconds
              </li>
              <li
                className={
                  'border-b border-brand-deep border-opacity-20 py-2 flex items-center gap-2'
                }
              >
                <img src={CartIcon} alt="Cart Icon" />
                Purchase qualifying products
              </li>
              <li
                className={
                  'border-b border-brand-deep border-opacity-20 py-2 flex items-center gap-2'
                }
              >
                <img src={InvoiceIcon} alt="Invoice Icon" />
                Submit your invoice online
              </li>
              <li className={'py-2 flex items-center gap-2'}>
                <img src={CashIcon} alt="Cash Icon" />
                Receive your payment within 30 days via mailed check or digital
                check
              </li>
            </ul>
          </div>
        </div>

        <div className={`flex flex-col ${SECTION_CARD} w-full`}>
          <div className="flex flex-col w-full">
            <div className="flex flex-col lg:flex-row justify-between items-center mb-10">
              <h2 className="text-3xl mb-5 lg:mb-0 text-center lg:text-left">
                Qualifying Products
              </h2>
              <div className="w-full lg:w-auto flex justify-center lg:justify-start flex-row gap-2">
                {data.offer?.learn_more_url && (
                  <button
                    className="bg-sage-lt text-[#4A4459] px-5 py-3 rounded-full flex-shrink-0"
                    onClick={() => {
                      window.open(data.offer?.learn_more_url, '_blank')
                    }}
                  >
                    Learn More
                  </button>
                )}
                <OfferStatusButton />
              </div>
            </div>
            {isPerProduct ? (
              <div className="flex flex-col gap-6 w-full">
                {products?.map((product: any) => {
                  const productTier = product.tiers?.[0]
                  const discount = productTier?.discount ?? 0
                  const min = productTier?.min ?? 0

                  return (
                    <div
                      key={product.product_id}
                      className="w-full rounded-xl outline outline-1 outline-gray-300 overflow-hidden"
                    >
                      <ProductGroupHeader
                        discountFormatted={formatOfferCurrency(discount)}
                        productName={product.product_name}
                        pillText={getPillText(min, productTier?.max)}
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 bg-white">
                        {product.product_variants.map((variant: any) => (
                          <VariantCard
                            key={`${product.product_id}-${variant.variant_id}`}
                            variant={variant}
                            hasAnyImage={hasAnyImage}
                            bordered={false}
                          />
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="flex flex-col lg:grid lg:grid-cols-2 flex-wrap gap-4 w-full">
                {products?.flatMap((product: any) =>
                  product.product_variants.map((variant: any) => (
                    <VariantCard
                      key={`${product.product_id}-${variant.variant_id}`}
                      variant={variant}
                      hasAnyImage={hasAnyImage}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </div>
        <div className="p-5 md:p-10">
          <h2 className="text-2xl mb-5">Offer Terms</h2>
          <ul className="mb-10 md:mb-20 list-disc pl-5">
            {isPerProduct ? (
              <>{termsString}</>
            ) : (
              <li className="mb-2">{termsString}</li>
            )}
            <li className="mb-2">
              Offer valid on cases purchased between{' '}
              {new Date(data.offer?.valid_from ?? '').toLocaleDateString(
                'en-US',
                {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                }
              )}{' '}
              and{' '}
              {new Date(data.offer?.valid_to ?? '').toLocaleDateString(
                'en-US',
                {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                }
              )}
            </li>
            <li className="mb-2">
              Invoices must be submitted by{' '}
              {new Date(
                new Date(data.offer?.valid_to ?? '').setMonth(
                  new Date(data.offer?.valid_to ?? '').getMonth() + 1
                )
              ).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
              —submit early and often to get your check sooner
            </li>
            <li className="mb-2">
              Invoices must show company name, address, date, product name,
              product number, and volume. Velocity reports or distributor recaps
              accepted; subject to additional verification
            </li>
          </ul>
          <div className="flex justify-center mb-8 md:hidden">
            <OfferStatusButton />
          </div>
          <a
            href="mailto:support@firstbite.io"
            className="bg-success-bright text-white rounded-lg p-2 font-mono text-sm block mx-auto text-center"
          >
            Questions? Contact support@firstbite.io
          </a>
        </div>
      </div>
    </div>
  )
}
